import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Typography, Stack, Paper, TextField,
  CircularProgress, LinearProgress, Chip, Box,
} from '@mui/material';
import {
  Close as CloseIcon, InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon, Settings as SettingsIcon,
} from '@mui/icons-material';

interface AgreementUploadDialogProps {
  open: boolean;
  isUploading: boolean;
  uploadProgress: number;
  isDragOver: boolean;
  stagedFile: File | null;
  setStagedFile: (f: File | null) => void;
  templateName: string;
  setTemplateName: (n: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onClose: () => void;
  onUpload: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onBrowse: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const AgreementUploadDialog = ({
  open, isUploading, uploadProgress, isDragOver,
  stagedFile, setStagedFile, templateName, setTemplateName,
  fileInputRef, onClose, onUpload, onDragOver, onDragLeave, onDrop, onBrowse,
}: AgreementUploadDialogProps) => {
  const handleClose = () => { if (!isUploading) onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Upload New Template</Typography>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            size="small" fullWidth label="Template Name" placeholder="Enter template name"
            value={templateName} onChange={(e) => setTemplateName(e.target.value)}
            disabled={isUploading}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem', borderRadius: 2 } }}
          />
          <Paper
            elevation={0}
            onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
            onClick={() => !isUploading && !stagedFile && fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : 'rgba(0,0,0,0.18)',
              borderRadius: '12px', p: 2.5,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
              cursor: isUploading || stagedFile ? 'default' : 'pointer',
              bgcolor: isDragOver ? 'primary.50' : 'background.default',
              transition: 'all 0.2s',
              '&:hover': stagedFile ? {} : { bgcolor: 'action.hover', borderColor: 'primary.light' },
            }}
          >
            <input ref={fileInputRef} type="file" style={{ display: 'none' }}
              accept=".pdf" onChange={onBrowse} disabled={isUploading} />
            {isUploading ? (
              <Stack alignItems="center" spacing={1} width="100%">
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>Uploading…</Typography>
                <Box width="100%" px={2}>
                  <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 2, height: 5 }} />
                </Box>
              </Stack>
            ) : stagedFile ? (
              <Stack alignItems="center" spacing={1} width="100%">
                <Chip
                  icon={<PdfIcon sx={{ fontSize: 18 }} />}
                  label={stagedFile.name}
                  onDelete={() => setStagedFile(null)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                  sx={{ maxWidth: '100%', fontSize: '0.78rem' }}
                />
                <Button variant="text" size="small" sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                  Choose a different file
                </Button>
              </Stack>
            ) : (
              <>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                </Box>
                <Typography variant="body2" fontWeight={600} align="center" sx={{ fontSize: '0.85rem' }}>Drag and drop files here</Typography>
                <Button variant="contained" size="small" sx={{ borderRadius: 2, px: 2.5, py: 0.5, fontSize: '0.78rem' }}
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                  Browse Files
                </Button>
                <Typography variant="caption" color="text.disabled" align="center" sx={{ fontSize: '0.7rem' }}>
                  PDF only · Max 25 MB
                </Typography>
              </>
            )}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={isUploading}>Cancel</Button>
        <Button
          variant="contained" disabled={!stagedFile || isUploading} onClick={onUpload}
          startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
        >
          {isUploading ? 'Uploading…' : 'Upload & Configure'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgreementUploadDialog;
